import {Box, Button, Grid, Input, Stack, Text} from "@chakra-ui/react";
import * as React from "react";
import {ChangeEvent, FocusEvent, KeyboardEvent, useContext, useEffect, useMemo, useState} from "react";
import {SoundAreaName, SoundFile, SoundFileLibraryEntry, Zone, ZoneSoundFiles} from "../../../components/types";
import {AppContext, AppContextI} from "../../../components/context/ContextWrapper";
import {useConfirmDialog} from "../../../components/common/ConfirmDialogProvider";
import {useLazyQuery, useMutation} from "@apollo/client";
import {registerSoundFileMutation, soundFileLibraryQuery} from "../../../components/soundfiles/soundFileQueries";
import {UploadFileMetadata} from "../../../components/upload/Upload";
import {UserContext, UserContextI} from "../../../UserContext";
import TimeCodeInput from "../../../components/form/TimeCodeInput";
import SoundFileLibraryDialog, {ExistingSoundOption} from "../../../components/soundfiles/SoundFileLibraryDialog";
import {useUploadQueueContext} from "../../../components/uploadQueue/UploadQueueContext";

type Props = {
    title: string
    soundName: SoundAreaName
    zone: Zone
    showFadeControls?: boolean
}

const AreaZoneSoundFile = ({title, soundName, zone, showFadeControls}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const userContext: UserContextI = useContext<UserContextI>(UserContext);
    const {confirm} = useConfirmDialog();
    const {enqueueUploads} = useUploadQueueContext();
    const [registerSoundFile] = useMutation(registerSoundFileMutation);
    const [loadSoundFileLibrary, {data: soundFileData, loading: soundFileLoading}] = useLazyQuery(soundFileLibraryQuery, {
        fetchPolicy: "network-only"
    });

    const soundFile = zone.soundFiles ? zone.soundFiles[soundName] : undefined;
    const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedExistingFile, setSelectedExistingFile] = useState<string | undefined>(undefined);
    const [fadeInTime, setFadeInTime] = useState<number>(soundFile?.fadeIn ?? 0);
    const [fadeOutTime, setFadeOutTime] = useState<number>(soundFile?.fadeOut ?? 0);

    const existingSoundOptions = useMemo<ExistingSoundOption[]>(() => {
        const libraryEntries = (soundFileData?.getSoundFileLibrary || []) as SoundFileLibraryEntry[];
        return libraryEntries.reduce((acc: ExistingSoundOption[], entry: SoundFileLibraryEntry) => {
            const fileName = entry?.objectId;
            if (!fileName) {
                return acc;
            }
            acc.push({
                sound: {
                    originalFileName: entry?.originalFileName,
                    fileName: fileName,
                    filePath: entry?.filePath,
                    playOnes: true,
                    fadeIn: 0,
                    fadeOut: 0,
                    delay: 0,
                    synchronize: false,
                    noSyncTime: 0,
                    volumeDb: 0
                },
                spotNames: [],
                libraryEntry: entry
            });
            return acc;
        }, []);
    }, [soundFileData]);

    useEffect(() => {
        if (isDialogOpen) {
            const areaId = appContext.getArea()?.id;
            if (areaId) {
                loadSoundFileLibrary({
                    variables: {
                        areaId: areaId
                    }
                });
            }
        }
    }, [appContext, isDialogOpen, loadSoundFileLibrary]);

    useEffect(() => {
        if (!isDialogOpen) {
            return;
        }
        if (soundFile?.fileName) {
            setSelectedExistingFile(soundFile.fileName);
            return;
        }
        if (!selectedExistingFile && existingSoundOptions.length > 0) {
            setSelectedExistingFile(existingSoundOptions[0].sound.fileName);
        }
    }, [existingSoundOptions, isDialogOpen, selectedExistingFile, soundFile?.fileName]);

    useEffect(() => {
        setFadeInTime(soundFile?.fadeIn ?? 0);
        setFadeOutTime(soundFile?.fadeOut ?? 0);
    }, [soundFile?.fadeIn, soundFile?.fadeOut, soundFile?.fileName]);

    const parseInputValue = (value: string): number => {
        if (value.length === 0) {
            return 0;
        } else {
            const intVal = parseInt(value, 10);
            return Number.isFinite(intVal) && intVal >= 0 ? intVal : 0;
        }
    };

    const updateFadeTimes = (nextFadeIn: number, nextFadeOut: number) => {
        if (!soundFile) {
            return;
        }
        if (nextFadeIn === soundFile.fadeIn && nextFadeOut === soundFile.fadeOut) {
            return;
        }
        const newSoundFiles: ZoneSoundFiles = {
            ...zone?.soundFiles
        };
        newSoundFiles[soundName] = {
            ...soundFile,
            fadeIn: nextFadeIn,
            fadeOut: nextFadeOut
        };
        appContext.updateZone(zone, {
            ...zone,
            soundFiles: newSoundFiles
        } as Zone);
    };

    const getDefaults = (): SoundFile => ({
        ambient: true,
        playOnes: false,
        fadeIn: 0,
        fadeOut: 0,
        delay: 0,
        synchronize: false,
        noSyncTime: 0,
        volumeDb: 0
    });

    const addOrReplaceSoundFile = (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        setDialogOpen(false);
        const nextSoundFile: SoundFile = {
            ...getDefaults(),
            ...(soundFile || {}),
            originalFileName: originalFileName,
            fileName: fileName,
            filePath: filePath,
            ambient: true
        };
        const newSoundFiles: ZoneSoundFiles = {
            ...zone?.soundFiles
        };
        newSoundFiles[soundName] = nextSoundFile;
        appContext.updateZone(zone, {
            ...zone,
            soundFiles: newSoundFiles
        } as Zone);

        const areaId = appContext.getArea()?.id;
        if (areaId) {
            const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
            registerSoundFile({
                variables: {
                    areaId: areaId,
                    objectId: fileName,
                    originalFileName: originalFileName,
                    filePath: filePath,
                    uploadedBy: uploadedBy,
                    mimeType: metadata?.mimeType,
                    sizeBytes: metadata?.sizeBytes,
                    durationMs: metadata?.durationMs
                }
            }).catch((error) => console.warn("Unable to register sound file", error));
        }
    };

    const registerUploadedFile = async (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        const areaId = appContext.getArea()?.id;
        if (!areaId) {
            return;
        }
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        try {
            await registerSoundFile({
                variables: {
                    areaId: areaId,
                    objectId: fileName,
                    originalFileName: originalFileName,
                    filePath: filePath,
                    uploadedBy: uploadedBy,
                    mimeType: metadata?.mimeType,
                    sizeBytes: metadata?.sizeBytes,
                    durationMs: metadata?.durationMs
                }
            });
        } catch (error) {
            console.warn("Unable to register sound file", error);
        }
        await loadSoundFileLibrary({variables: {areaId}});
    };

    const addExistingSoundFile = () => {
        if (!selectedExistingFile) {
            return;
        }
        const selected = existingSoundOptions.find((opt: ExistingSoundOption) => opt.sound.fileName === selectedExistingFile);
        if (!selected) {
            return;
        }
        const nextSoundFile: SoundFile = {
            ...getDefaults(),
            ...(soundFile || {}),
            originalFileName: selected.sound.originalFileName,
            fileName: selected.sound.fileName,
            filePath: selected.sound.filePath,
            ambient: true
        };
        const newSoundFiles: ZoneSoundFiles = {
            ...zone?.soundFiles
        };
        newSoundFiles[soundName] = nextSoundFile;
        appContext.updateZone(zone, {
            ...zone,
            soundFiles: newSoundFiles
        } as Zone);
        setDialogOpen(false);
    };

    const openDialog = (preferredFileName?: string) => {
        if (preferredFileName) {
            setSelectedExistingFile(preferredFileName);
        } else if (existingSoundOptions.length > 0) {
            setSelectedExistingFile(existingSoundOptions[0].sound.fileName);
        } else {
            setSelectedExistingFile(undefined);
        }
        setDialogOpen(true);
    };

    const clearSoundFile = async (soundAreaName: SoundAreaName) => {
        const approved = await confirm({
            title: "Delete sound file",
            body: "Are you sure you want to delete this file?",
            confirmText: "Delete",
            isDestructive: true
        });
        if (approved) {
            const newSoundFiles: ZoneSoundFiles = {
                ...zone?.soundFiles,
            };
            newSoundFiles[soundAreaName] = undefined;

            appContext.updateZone(zone, {
                ...zone,
                soundFiles: newSoundFiles
            } as Zone)
        }
    }


    return <Box>
        <Text fontWeight={"semibold"}>{title}</Text>
        {

            soundFile?.originalFileName
        }
        <Stack direction={"row"}>
            <Button size={"sm"} onClick={() => openDialog(soundFile?.fileName)} isDisabled={!appContext.isDraft()}>
                {soundFile ? "Replace file" : "Upload / select file"}
            </Button>
            {soundFile ?
                <Button size={"sm"}
                        onClick={() => clearSoundFile(soundName)}
                        disabled={!appContext.isDraft()}
                >
                    Clear
                </Button>
                : null
            }
        </Stack>
        {showFadeControls ? (
            <Grid
                gridTemplateColumns={"max-content max-content"}
                columnGap={"16px"}
                rowGap={"6px"}
                marginTop={"12px"}
            >
                <Box>
                    <Text fontSize={"sm"} fontWeight={"semibold"}>Fade in</Text>
                    <Box display={"flex"} alignItems={"center"} gap={2}>
                        <TimeCodeInput
                            valueMs={fadeInTime}
                            onChangeMs={(nextValue) => {
                                setFadeInTime(nextValue);
                                updateFadeTimes(nextValue, fadeOutTime);
                            }}
                            isDisabled={!soundFile || !appContext.isDraft()}
                        />
                        <Text fontSize={"xs"} color={"gray.500"}>mm:ss.t</Text>
                    </Box>
                </Box>
                <Box>
                    <Text fontSize={"sm"} fontWeight={"semibold"}>Fade out</Text>
                    <Box display={"flex"} alignItems={"center"} gap={2}>
                        <TimeCodeInput
                            valueMs={fadeOutTime}
                            onChangeMs={(nextValue) => {
                                setFadeOutTime(nextValue);
                                updateFadeTimes(fadeInTime, nextValue);
                            }}
                            isDisabled={!soundFile || !appContext.isDraft()}
                        />
                        <Text fontSize={"xs"} color={"gray.500"}>mm:ss.t</Text>
                    </Box>
                </Box>
            </Grid>
        ) : null}
        <SoundFileLibraryDialog
            isOpen={isDialogOpen}
            onClose={() => setDialogOpen(false)}
            mode={"slot"}
            existingSoundOptions={existingSoundOptions}
            isLoading={soundFileLoading}
            selectedExistingFile={selectedExistingFile}
            onSelectExistingFile={setSelectedExistingFile}
            onAddExisting={addExistingSoundFile}
            onUpload={(originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
                addOrReplaceSoundFile(originalFileName, fileName, filePath, metadata);
            }}
            registerUploadedFile={registerUploadedFile}
            enqueueUploads={enqueueUploads}
            isReplace={!!soundFile}
        />
    </Box>
}

export default AreaZoneSoundFile
